import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';

export default function EmailVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmail } = useApp();

  const { userId, code: demoCode, email } = location.state || {};
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const refs = useRef([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const handleChange = (i, val) => {
    const v = val.replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) refs.current[i + 1]?.focus();
    if (next.every(d => d !== '')) {
      setTimeout(() => handleVerify(next.join('')), 50);
    }
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  const handleVerify = (codeStr) => {
    const entered = codeStr || digits.join('');
    if (entered.length < 6) { setError('Please enter the full 6-digit code.'); return; }
    const result = verifyEmail(userId, entered);
    if (result.error) {
      setError(result.error);
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => refs.current[0]?.focus(), 50);
      return;
    }
    setSuccess(true);
    setTimeout(() => navigate('/login'), 2000);
  };

  if (!userId) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <p style={{ color: 'var(--gray-400)' }}>Invalid verification link.</p>
        <button className="btn btn-primary" onClick={() => navigate('/register')} style={{ marginTop: 16 }}>
          Back to Register
        </button>
      </div>
    );
  }

  return (
    <div className="page" style={{ paddingTop: 60 }}>
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'var(--accent-light)', border: '2px solid var(--accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
          fontSize: 32,
        }}>
          ✉️
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 10 }}>Check your email</h1>
        <p style={{ color: 'var(--gray-400)', fontSize: 14, lineHeight: 1.6 }}>
          We sent a 6-digit verification code to<br/>
          <strong style={{ color: 'var(--white)' }}>{email}</strong>
        </p>
      </div>

      {success ? (
        <div className="alert alert-success" style={{ textAlign: 'center', fontSize: 15, padding: 20 }}>
          ✅ Email verified! Redirecting to login...
        </div>
      ) : (
        <>
          {error && <div className="alert alert-error">{error}</div>}

          {/* Demo hint */}
          {demoCode && (
            <div className="alert alert-info" style={{ marginBottom: 20 }}>
              <strong>Demo mode:</strong> Your code is <strong>{demoCode}</strong>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 28 }}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={el => (refs.current[i] = el)}
                type="text" inputMode="numeric" maxLength={1}
                value={d}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                style={{
                  width: 48, height: 56, textAlign: 'center',
                  fontSize: 24, fontWeight: 700,
                  background: 'var(--navy-mid)',
                  border: `2px solid ${d ? 'var(--accent)' : 'var(--navy-border)'}`,
                  borderRadius: 10, color: 'var(--white)',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
              />
            ))}
          </div>

          <button className="btn btn-primary btn-full" onClick={() => handleVerify()}
            style={{ padding: '14px', fontSize: 15 }}>
            Verify Email
          </button>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--gray-500)' }}>
            Didn't receive it? Check your spam folder.
          </p>
        </>
      )}
    </div>
  );
}
